import React from "react";

/**
 * BdrSectionPanel — a titled section: a BLUE header bar over a GRAY body.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE CSS CAME FIRST. `.bdr-section-panel*` has been in `index.css` since
 * 2026-10-05 and eleven surfaces hand-write its three divs. This component
 * does not replace those classes — it renders them — so the two can coexist
 * and the existing pages migrate when they are next touched rather than in one
 * sweep. New surfaces should use this.
 *
 * WHAT HAND-WRITING COST, and why the component exists:
 *
 *   · The HEADING LEVEL drifted to five different values for one visual
 *     element — h1, h2, h3, h4 and h5 were all in use for the same bar, which
 *     makes the document outline meaningless to anyone navigating by heading.
 *     `titleAs` defaults to `h2` here, so the level is a decision rather than
 *     whatever the author happened to type. (The admin nav owns the page's
 *     only `h1`; a panel title is a section under it.)
 *   · The panel's own text colour was never declared, so every page relied on
 *     an ancestor supplying white. The first page whose wrapper did not
 *     rendered its title in Bootstrap's near-black on navy. Fixed in the CSS,
 *     but it is the kind of thing a component stops happening again.
 *   · `--scroll` on the body is easy to forget, and forgetting it means the
 *     PAGE scrolls instead of the panel, which is silent until someone notices
 *     the header scrolled away.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE HEADER'S SECOND SLOT IS FREE-FORM, deliberately. Across the existing
 * surfaces it holds tabs (Business Ledger, Image Groups), a filter bar
 * (Referrals, Job Forms, Stuck Submissions), a date (Today), and plain action
 * buttons (Style Guide). Those are not all "actions", so `headerExtra` takes
 * any node and `actions` is the narrower convenience that wraps its children
 * in `.bdr-section-panel-actions` for the common right-aligned-buttons case.
 *
 * A panel with NO title is legal — the Tasks console has one — in which case
 * the header is not rendered at all rather than left as an empty bar.
 */

const BdrSectionPanel = ({
  /** Header text. Omit it and no header renders. */
  title,
  /**
   * The heading element. `h2` by default: the admin nav is the page's `h1`,
   * so a panel title is a section beneath it. Pass `h3` for a panel nested
   * inside another page section.
   */
  titleAs: TitleTag = "h2",
  /** Right-aligned controls; wrapped in `.bdr-section-panel-actions`. */
  actions,
  /** Anything else in the header — a tab strip, a filter bar, a date. Raw. */
  headerExtra,
  /** `true` makes the BODY the scrolling element instead of the page. */
  scroll = false,
  /** Extra classes on the frame. */
  className = "",
  /** Extra classes on the header. */
  headerClassName = "",
  /** Extra classes on the body. */
  bodyClassName = "",
  children,
  ...rest
}) => {
  const hasHeader = title != null || actions != null || headerExtra != null;

  return (
    <div className={`bdr-section-panel ${className}`.trim()} {...rest}>
      {hasHeader && (
        <div className={`bdr-section-panel-hdr ${headerClassName}`.trim()}>
          {title != null && (
            <TitleTag className="bdr-section-panel-title">{title}</TitleTag>
          )}
          {actions != null && (
            <div className="bdr-section-panel-actions">{actions}</div>
          )}
          {headerExtra}
        </div>
      )}

      <div
        className={[
          "bdr-section-panel-body",
          scroll ? "bdr-section-panel-body--scroll" : "",
          bodyClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </div>
    </div>
  );
};

export default BdrSectionPanel;
